/**
 * Página de la ruta /exclusive (FullscreenExclusive.tsx = /exclusive).
 * Video Mux + Stripe checkout a $39.99 USD.
 *
 * Comportamiento exclusivo de esta ruta (no afecta a /sorpresa, /canal, etc.):
 * - Sin aviso "activar sonido" (hideUnmutePrompt solo aquí).
 * - Countdown global de 2h (mismo fin para todos, desde EXCLUSIVE_COUNTDOWN_END).
 * - Descuento 25% aplicado en checkout (cupón EXCLUSIVO25), no sobre el video.
 *
 * Playback ID y countdown: /api/public-playback-ids (EXCLUSIVE_PLAYBACK_ID, EXCLUSIVE_COUNTDOWN_END).
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import MuxPosterBackdrop from "@/components/MuxPosterBackdrop";
import { MembershipFloatingCta } from "@/components/MembershipFloatingCta";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useEffect, useState } from "react";

// Fallback: env del brand. Sin literales del fork anterior. Si todas vacías,
// el componente downstream maneja "" gracefully (no video, CTAs siguen).
const FALLBACK_PLAYBACK_ID =
  (import.meta.env.VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID as string | undefined)?.trim() ||
  (import.meta.env.VITE_PREMIUM_MUX_ES_VIP as string | undefined)?.trim() ||
  "";

const buildTimePlaybackId =
  (import.meta.env.VITE_EXCLUSIVE_PLAYBACK_ID as string)?.trim() ||
  FALLBACK_PLAYBACK_ID;

function useCountdown(endAtIso: string | null) {
  const [remaining, setRemaining] = useState<{
    h: number;
    m: number;
    s: number;
  } | null>(null);

  useEffect(() => {
    if (!endAtIso || typeof endAtIso !== "string") return;
    const end = new Date(endAtIso).getTime();
    if (Number.isNaN(end)) return;

    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((end - now) / 1000));
      if (diff <= 0) {
        setRemaining(null);
        return;
      }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setRemaining({ h, m, s });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endAtIso]);

  return remaining;
}

export default function FullscreenExclusive() {
  const [playbackId, setPlaybackId] = useState(buildTimePlaybackId);
  const [countdownEnd, setCountdownEnd] = useState<string | null>(null);
  // Audit 2026-06-14: poster blur-up (coherente con DualCTA).
  const [videoPlaying, setVideoPlaying] = useState(false);
  const { setTemplateName } = useCurrentTemplate();
  const countdown = useCountdown(countdownEnd);

  useEffect(() => {
    setTemplateName(null);
    return () => setTemplateName(null);
  }, [setTemplateName]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
    };
  }, []);

  useEffect(() => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    if (!base) return;
    fetch(`${base}/api/public-playback-ids`)
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (
          data: { exclusive?: string; exclusiveCountdownEnd?: string } | null,
        ) => {
          const id = data?.exclusive?.trim();
          if (id) setPlaybackId(id);
          const end = data?.exclusiveCountdownEnd?.trim();
          if (end) setCountdownEnd(end);
        },
      )
      .catch(() => {});
  }, []);

  return (
    <div className="w-full h-dvh max-h-dvh overflow-hidden bg-black relative">
      {/* Countdown global de 2h (mismo para todos) */}
      {countdown && (
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-xl bg-black/70 text-white text-center backdrop-blur-sm border border-white/20"
          aria-live="polite"
          aria-label={`Tiempo restante ${countdown.h}:${String(countdown.m).padStart(2, "0")}:${String(countdown.s).padStart(2, "0")}`}
        >
          <span className="text-xs uppercase tracking-wider text-white/80 block mb-0.5">
            Termina en
          </span>
          <span className="font-body text-lg font-bold tabular-nums tracking-tight">
            {String(countdown.h).padStart(2, "0")}:
            {String(countdown.m).padStart(2, "0")}:
            {String(countdown.s).padStart(2, "0")}
          </span>
        </div>
      )}

      <MuxPosterBackdrop
        key={`poster-${playbackId}`}
        playbackId={playbackId}
        fitMode="smartcrop"
        hidden={videoPlaying}
      />
      <MuxVideoPlayerWithCTA
        playbackId={playbackId}
        overridePackId="exclusive"
        variant="fullscreen"
        ctaLabel="Descubrir"
        ctaSubtitle="Ingresar al Canal Privado + Hablar conmigo"
        hideUnmutePrompt
        onPlay={() => setVideoPlaying(true)}
      />
      <MembershipFloatingCta />
    </div>
  );
}
